<script lang="ts">
  /**
   * The register itself.
   *
   * Grouped by category rather than listed flat: the question people actually
   * arrive with is "what have we got for water" or "when was the inverter put
   * in", never "show me all 40 things alphabetically".
   */
  import { Icon, zar, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";

  type Asset = {
    id: string;
    name: string;
    subtitle: string | null;
    category: string;
    room: string | null;
    make: string | null;
    model: string | null;
    serial: string | null;
    installedAt: string | null;
    purchasePrice: number | null;
    supplier: string | null;
    warrantyEndsAt: string | null;
    replacementValue: number | null;
    insured: boolean;
    status: string;
    notes: string | null;
  };

  const CATEGORIES = ["power", "water", "climate", "security", "appliance", "structure", "vehicle"];

  let assets = $state<Asset[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let adding = $state(false);
  let editingId = $state<string | null>(null);
  let saving = $state(false);

  type Draft = {
    name: string; subtitle: string; category: string; room: string;
    make: string; model: string; serial: string; installedAt: string;
    purchasePrice: string; supplier: string; warrantyEndsAt: string;
    replacementValue: string; insured: boolean; notes: string;
  };
  const EMPTY: Draft = {
    name: "", subtitle: "", category: "appliance", room: "", make: "", model: "",
    serial: "", installedAt: "", purchasePrice: "", supplier: "", warrantyEndsAt: "",
    replacementValue: "", insured: false, notes: "",
  };
  let draft = $state<Draft>({ ...EMPTY });

  const byCategory = $derived(
    CATEGORIES.map((c) => ({ category: c, rows: assets.filter((a) => a.category === c) })).filter(
      (g) => g.rows.length > 0,
    ),
  );

  // Replacement value, not purchase price: it is the number the insurer asks for
  // and the one that is wrong on every household schedule.
  //
  // Null rather than 0 when nothing carries a value, so `zar()` renders an em
  // dash. The day-one register ships with no replacement values at all — the
  // spec records none and inventing them would put a figure on the insurance
  // line that reads exactly like a real one. "Insured to R 0" is a claim; "—"
  // is the truth, which is that nobody has filled this in yet.
  const valued = $derived(assets.filter((a) => a.insured && a.replacementValue != null));
  const insuredTotal = $derived(
    valued.length === 0 ? null : valued.reduce((t, a) => t + (a.replacementValue ?? 0), 0),
  );
  const uninsured = $derived(assets.filter((a) => !a.insured && (a.replacementValue ?? 0) > 0));

  async function load() {
    loading = true;
    error = null;
    try {
      assets = (await api.get<{ assets: Asset[] }>("/assets")).assets;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not load the register.";
    } finally {
      loading = false;
    }
  }
  void load();

  function startAdd() {
    draft = { ...EMPTY };
    editingId = null;
    adding = true;
  }

  function startEdit(a: Asset) {
    adding = false;
    editingId = a.id;
    draft = {
      name: a.name,
      subtitle: a.subtitle ?? "",
      category: a.category,
      room: a.room ?? "",
      make: a.make ?? "",
      model: a.model ?? "",
      serial: a.serial ?? "",
      // <input type="date"> speaks yyyy-mm-dd only; anything else renders blank
      // and silently discards the value the record already had.
      installedAt: (a.installedAt ?? "").slice(0, 10),
      purchasePrice: a.purchasePrice == null ? "" : String(a.purchasePrice),
      supplier: a.supplier ?? "",
      warrantyEndsAt: (a.warrantyEndsAt ?? "").slice(0, 10),
      replacementValue: a.replacementValue == null ? "" : String(a.replacementValue),
      insured: a.insured,
      notes: a.notes ?? "",
    };
  }

  function cancel() {
    adding = false;
    editingId = null;
    error = null;
  }

  async function save() {
    if (!draft.name.trim()) {
      error = "A name is required.";
      return;
    }
    saving = true;
    error = null;
    const body = {
      name: draft.name.trim(),
      subtitle: draft.subtitle.trim() || null,
      category: draft.category,
      room: draft.room.trim() || null,
      make: draft.make.trim() || null,
      model: draft.model.trim() || null,
      serial: draft.serial.trim() || null,
      installedAt: draft.installedAt || null,
      purchasePrice: draft.purchasePrice.trim() === "" ? null : Number(draft.purchasePrice),
      supplier: draft.supplier.trim() || null,
      warrantyEndsAt: draft.warrantyEndsAt || null,
      replacementValue: draft.replacementValue.trim() === "" ? null : Number(draft.replacementValue),
      insured: draft.insured,
      notes: draft.notes.trim() || null,
    };
    try {
      if (editingId) await api.patch(`/assets/${editingId}`, body);
      else await api.post("/assets", body);
      cancel();
      await load();
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not save that.";
    } finally {
      saving = false;
    }
  }

  const pretty = (s: string) => s.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
</script>

<div class="wrap">
  <section class="card">
    <div class="head">
      <span class="kicker">What we own</span>
      <span class="sub">{assets.length} {assets.length === 1 ? "item" : "items"}</span>
    </div>

    {#if loading}
      <p class="sub pad">Loading the register…</p>
    {:else if error && !adding && !editingId}
      <p class="err pad"><Icon name="alert" size={12} /> {error}</p>
    {:else if assets.length === 0}
      <p class="sub pad">
        Nothing on the register yet. Add the things that would be expensive to replace and
        annoying to remember — the inverter, the pump, the geyser.
      </p>
    {:else}
      <!-- Cover first: it is the one number here with a consequence, and an
           under-insured house is the failure this register exists to prevent. -->
      <div class="cover">
        <span class="pill">
          {#if insuredTotal === null}
            <Icon name="pause" size={11} /> No replacement values recorded yet
          {:else}
            Insured to {zar(insuredTotal)}
          {/if}
        </span>
        {#if uninsured.length > 0}
          <span class="pill pill--warn">
            <Icon name="alert" size={11} />
            {uninsured.length} with a value but no cover
          </span>
        {/if}
      </div>

      {#each byCategory as g (g.category)}
        <div class="group">
          <div class="group__h">{pretty(g.category)}</div>
          {#each g.rows as a (a.id)}
            <div class="row">
              <div class="grow">
                <div class="row__t">
                  {a.name}
                  {#if a.status === "retired"}<span class="pill pill--dim">retired</span>{/if}
                </div>
                <div class="chips">
                  {#if a.subtitle}<span class="pill pill--quiet">{a.subtitle}</span>{/if}
                  {#if a.room}<span class="pill pill--quiet">{a.room}</span>{/if}
                  {#if a.installedAt}
                    <span class="pill pill--quiet num">in since {dateZA(a.installedAt)}</span>
                  {/if}
                  {#if a.replacementValue != null}
                    <span class="pill pill--quiet num">{zar(a.replacementValue)}</span>
                  {/if}
                  {#if a.insured}
                    <span class="pill pill--ok"><Icon name="shield" size={10} /> insured</span>
                  {/if}
                </div>
                <!-- Notes were captured and never shown. The day-one register puts
                     the real service cadences here, and for the four assets whose
                     install month is genuinely unknown it is where the year lives
                     — so hiding this line was hiding the honest half of the row. -->
                {#if a.notes}<div class="note">{a.notes}</div>{/if}
              </div>
              <button class="lnk" onclick={() => (editingId === a.id ? cancel() : startEdit(a))}>
                {editingId === a.id ? "Close" : "Edit"}
              </button>
            </div>

            {#if editingId === a.id}
              {@render form()}
            {/if}
          {/each}
        </div>
      {/each}
    {/if}

    {#if adding}
      {@render form()}
    {:else if !editingId}
      <div class="acts">
        <button class="btn-primary" onclick={startAdd}>Add something</button>
      </div>
    {/if}
  </section>
</div>

{#snippet form()}
  <div class="edit">
    <div class="grid">
      <label class="f f--wide">
        <span>Name</span>
        <input bind:value={draft.name} placeholder="Victron MultiPlus-II" />
      </label>
      <label class="f">
        <span>What it is</span>
        <input bind:value={draft.subtitle} placeholder="inverter" />
      </label>
      <label class="f">
        <span>Category</span>
        <select bind:value={draft.category}>
          {#each CATEGORIES as c (c)}<option value={c}>{pretty(c)}</option>{/each}
        </select>
      </label>
      <label class="f"><span>Room</span><input bind:value={draft.room} /></label>
      <label class="f"><span>Make</span><input bind:value={draft.make} /></label>
      <label class="f"><span>Model</span><input bind:value={draft.model} /></label>
      <label class="f"><span>Serial</span><input bind:value={draft.serial} /></label>
      <label class="f">
        <span>Installed</span>
        <input type="date" bind:value={draft.installedAt} />
      </label>
      <label class="f">
        <span>Warranty ends</span>
        <input type="date" bind:value={draft.warrantyEndsAt} />
      </label>
      <label class="f">
        <span>Paid (R)</span>
        <input type="number" step="0.01" bind:value={draft.purchasePrice} />
      </label>
      <label class="f">
        <span>Replace for (R)</span>
        <input type="number" step="0.01" bind:value={draft.replacementValue} />
      </label>
      <label class="f"><span>Supplier</span><input bind:value={draft.supplier} /></label>
      <label class="f f--wide"><span>Notes</span><input bind:value={draft.notes} /></label>
    </div>

    <label class="chk">
      <input type="checkbox" bind:checked={draft.insured} />
      <span>It is on the household insurance schedule</span>
    </label>

    {#if error}
      <p class="err"><Icon name="alert" size={12} /> {error}</p>
    {/if}

    <div class="acts">
      <button class="lnk" onclick={cancel} disabled={saving}>Cancel</button>
      <button class="btn-primary" onclick={save} disabled={saving}>
        {saving ? "Saving…" : editingId ? "Save changes" : "Add it"}
      </button>
    </div>
  </div>
{/snippet}

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card {
    padding: 16px 18px 18px;
    border: 1px solid var(--ln);
    border-radius: var(--r-card, 12px);
    background: var(--surface-1, var(--bg));
  }
  .head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; }
  .kicker { font-size: 11px; font-weight: 800; letter-spacing: 0.8px; text-transform: uppercase; color: var(--acc); }
  .pad { padding: 8px 0; }
  .cover { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
  .group { margin-bottom: 14px; }
  .group__h {
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--mut);
  }
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid var(--ln);
    border-radius: var(--r-control);
    margin-bottom: 6px;
  }
  .grow { flex: 1; min-width: 0; }
  .row__t { font-size: 13px; font-weight: 700; color: var(--tx); }
  .chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 5px; }
  .note { margin-top: 5px; font-size: var(--fs-micro); color: var(--mut); line-height: 1.5; }
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    font-size: 10.5px;
    font-weight: 700;
    border-radius: 999px;
    background: var(--surface-2, var(--bg));
    border: 1px solid var(--ln);
    color: var(--tx);
  }
  .pill--quiet { color: var(--mut); }
  .pill--dim { color: var(--mut); opacity: 0.7; }
  /* Blue for good and amber for attention — never green-vs-red, and the glyph
     and wording carry the meaning without the colour. */
  .pill--ok { color: var(--ok, var(--acc)); }
  .pill--warn { color: var(--warn); }
  .num { font-variant-numeric: tabular-nums; }
  .edit {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0 0 10px;
    padding: 14px 16px 16px;
    border: 1px solid var(--ln);
    border-radius: var(--r-card, 12px);
    background: var(--surface-2, var(--bg));
  }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; }
  .f { display: flex; flex-direction: column; gap: 4px; font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .f--wide { grid-column: 1 / -1; }
  .f input, .f select {
    padding: 8px 10px;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--tx);
    background: var(--bg);
    border: 1px solid var(--ln);
    border-radius: var(--r-control);
  }
  .chk { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--mut); }
  .acts { display: flex; justify-content: flex-end; align-items: center; gap: 12px; margin-top: 4px; }
  .lnk { font-size: 11.5px; font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }
  .err {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: 0;
    font-size: 11.5px;
    font-weight: 700;
    color: var(--warn);
  }
  .sub { font-size: 11.5px; color: var(--mut); }
</style>
